import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { adminApi, api, errMsg } from "@/lib/api";
import { useSite } from "@/lib/store";

export function useSiteDraft(fields) {
  const { reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const pick = (d) => Object.fromEntries(fields.map((f) => [f, structuredClone(d[f])]));
  useEffect(() => {
    api.get("/site").then((r) => setDraft(pick(r.data)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/site", draft);
      setDraft(pick(r.data));
      reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };
  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return { draft, patch, save, saving };
}

export const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

export function PageHead({ title, desc, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-head font-extrabold text-3xl tracking-tight" data-testid="admin-page-title">{title}</h1>
        {desc && <p className="text-sm text-zinc-500 mt-1.5 max-w-2xl">{desc}</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`bg-white border border-zinc-200 p-5 sm:p-6 ${className}`} {...rest}>{children}</div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="label-caps text-[10px] text-zinc-500">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <span className="text-[11px] text-zinc-400 mt-1 block">{hint}</span>}
  </label>
);

export const Btn = ({ variant = "dark", className = "", ...props }) => {
  const v = {
    dark: "bg-black text-white hover:bg-[#dc1f26]",
    red: "bg-red text-white hover:bg-[#b8161c]",
    outline: "border border-zinc-300 bg-white hover:border-black",
    ghost: "hover:bg-zinc-100",
  }[variant];
  return <button className={`px-4 h-10 text-xs font-bold tracking-[0.1em] uppercase disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2 ${v} ${className}`} {...props} />;
};

export const SaveBtn = ({ save, saving, testid = "admin-save-button" }) => (
  <Btn variant="red" onClick={save} disabled={saving} data-testid={testid}>{saving ? "Saving…" : "Save changes"}</Btn>
);

export function RowTools({ i, len, onMove, onDelete, testid }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <button type="button" disabled={i === 0} onClick={() => onMove(-1)} className="p-1.5 hover:bg-zinc-100 disabled:opacity-25" data-testid={`${testid}-up`}><ArrowUp className="w-4 h-4" /></button>
      <button type="button" disabled={i === len - 1} onClick={() => onMove(1)} className="p-1.5 hover:bg-zinc-100 disabled:opacity-25" data-testid={`${testid}-down`}><ArrowDown className="w-4 h-4" /></button>
      {onDelete && <button type="button" onClick={onDelete} className="p-1.5 hover:bg-red-50 text-red" data-testid={`${testid}-delete`}><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

export const inputCls = "w-full h-10 px-3 border border-zinc-300 bg-white text-sm outline-none focus:border-black transition-colors";
export const Loading = () => <div className="h-64 bg-white border border-zinc-200 animate-pulse" />;
